import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import { lightImpactVibrate, postCTA } from '../../../utils/appBridge'
import { useNewEliteLandingAnalyticsScreen } from '../../../features/new-elite-landing/analytics/NewEliteLandingAnalyticsContext'
import { NewEliteLandingEventId, trackNewEliteUserClicked } from '../../../features/new-elite-landing/analytics/newEliteLandingAnalytics'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { EliteSpecialOfferGiftBanner } from '../EliteSpecialOfferGiftBanner'
import NewEliteLandingPlanSelector, {
  isPriceBasedSpecialOffer,
  isTenureBasedSpecialOffer,
  isYearlyPlanTenure,
  NewEliteLandingPrimaryCtaButton,
  resolvePlanId,
  resolveRenewalAwareSupportText,
  resolveSupportTextDisplayHtml,
  resolveTenureBasedBaseMonths,
  resolveTenureBasedExtraMonths,
  updateCtaWithSelectedPlanId
} from '../../../features/new-elite-landing/components/NewEliteLandingPlanSelector'
import RenderHTML from '../../ui-components/RenderHTML'
import { getBottomPadding } from '../../../utils/sessionStorage'
import type { EliteLandingPlan } from '../../../features/new-elite-landing/planTypes'
import { getElitePlanDisplayFeesExGst } from './eliteOnboardingFocusPlan'
import { FamilyOnboardingViewProps } from '../../../features/new-elite-landing/pages/family/familyOnboardingViewProps'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'

export function EliteMembershipRenewalSection({
  familyConfig,
  eliteOnboardingStatus
}: {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  familyConfig?: FamilyOnboardingViewProps
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eligiblePlans = eliteOnboardingStatus?.eligiblePlans ?? []
  const [selectedPlan, setSelectedPlan] = useState<EliteLandingPlan>('yearly')
  const [isInlineRenewCtaVisible, setIsInlineRenewCtaVisible] = useState(true)
  const inlineRenewCtaRef = useRef<HTMLDivElement | null>(null)
  const cta = eliteOnboardingStatus?.renewal?.cta

  useEffect(() => {
    if (eligiblePlans.length === 0) return
    if (eligiblePlans.some((plan) => isYearlyPlanTenure(plan?.effectivePlanTenure ?? plan?.basePlanTenure))) {
      setSelectedPlan('yearly')
      return
    }
    if (eligiblePlans.some((plan) => (plan?.effectivePlanTenure ?? plan?.basePlanTenure)?.period === 'MONTH')) {
      setSelectedPlan('monthly')
    }
  }, [eligiblePlans])

  const handleRenewClick = () => {
    lightImpactVibrate()
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.RenewalCta, getEliteBuyCtaEventExtras(eliteOnboardingStatus))
    if (!cta?.url) return
    const onlyPlan = eligiblePlans.length === 1 ? eligiblePlans[0] : undefined
    const selectedPlanId = resolvePlanId(onlyPlan)
    if (!selectedPlanId) {
      postCTA(JSON.stringify(cta))
      return
    }
    postCTA(JSON.stringify(updateCtaWithSelectedPlanId(cta, selectedPlanId)))
  }

  const singleEligiblePlan = eligiblePlans.length === 1 ? eligiblePlans[0] : undefined
  const renewAmount = singleEligiblePlan != null ? getElitePlanDisplayFeesExGst(singleEligiblePlan) : 0
  const planTenurePeriod =
    singleEligiblePlan?.effectivePlanTenure?.period ?? singleEligiblePlan?.basePlanTenure?.period
  const planTenureValue = Number(
    singleEligiblePlan?.effectivePlanTenure?.value ?? singleEligiblePlan?.basePlanTenure?.value ?? 0
  )
  const planSuffix = planTenurePeriod === 'YEAR' || planTenureValue >= 12 ? '/yr' : '/mo'
  const showPlanSelector = eligiblePlans.length > 1
  const isTenureOffer = isTenureBasedSpecialOffer(singleEligiblePlan)
  const isPriceOffer = isPriceBasedSpecialOffer(singleEligiblePlan)
  const showGiftOfferBanner = isTenureOffer || isPriceOffer
  const renewCtaLabel = `Renew ${familyConfig?.isFamilyMode ? 'their ' : ''}Elite ${selectedPlan} @ ${roundOffAndFormatNumberWithCurrency(renewAmount)}${planSuffix} +GST`
  const isRenewCtaDisabled = !cta?.url
  const renewalSupportDisplayHtml = resolveSupportTextDisplayHtml(
    resolveRenewalAwareSupportText(undefined, eliteOnboardingStatus?.renewal?.status),
    eliteOnboardingStatus?.subscriptionDetails?.activationExpiryDate
  )

  useEffect(() => {
    if (showPlanSelector) {
      setIsInlineRenewCtaVisible(true)
      return
    }

    let observer: IntersectionObserver | undefined
    const frameId = requestAnimationFrame(() => {
      const inlineCtaNode = inlineRenewCtaRef.current
      if (!inlineCtaNode) return

      observer = new IntersectionObserver(
        (entries) => {
          setIsInlineRenewCtaVisible(entries?.[0]?.isIntersecting ?? false)
        },
        { threshold: 0.2 }
      )
      observer.observe(inlineCtaNode)
    })

    return () => {
      cancelAnimationFrame(frameId)
      observer?.disconnect()
    }
  }, [showPlanSelector, renewCtaLabel, isRenewCtaDisabled])

  return showPlanSelector ? (
    <NewEliteLandingPlanSelector
      familyConfig={familyConfig}
      selectedPlan={selectedPlan}
      onPlanChange={setSelectedPlan}
      eligiblePlans={eligiblePlans}
      cta={cta}
      eliteLandingPageState={eliteOnboardingStatus?.eliteLandingPageState}
      selectedPlanDetails={eliteOnboardingStatus?.selectedPlanDetails}
      activationExpiryDate={eliteOnboardingStatus?.subscriptionDetails?.activationExpiryDate}
      autoPayStatus={eliteOnboardingStatus?.subscriptionDetails?.autoPayStatus}
      renewalStatus={eliteOnboardingStatus?.renewal?.status}
      consentText={eliteOnboardingStatus?.consentText}
      consentTextList={eliteOnboardingStatus?.consentTextList}
      isStatusLoading={false}
      eliteRenewalFlow
      enableFloatingPrimaryCta
      referralReward={eliteOnboardingStatus?.referralReward}
    />

  ) : (
    <>
      <div
        className={`relative z-10 mx-5 overflow-hidden rounded-xl bg-gradient-to-r from-[#CC5C2D] to-[#D16B3F] text-center text-[13px] text-white`}
      >
        {
          familyConfig?.isFamilyMode ? (
            <div className='px-4 py-2.5 font-medium leading-[1.3]'>
              <p className='text-[11px] leading-[1.2] tracking-[0.04em]'>{familyConfig.familyOrangeStripText?.headline}</p>
              <p className='mt-1 text-[13px] leading-[1.4] font-medium'>
                {familyConfig.familyOrangeStripText?.subline}
              </p>
            </div>
          ) : null
        }
        {showGiftOfferBanner ? (
          <EliteSpecialOfferGiftBanner
            variant={isTenureOffer ? 'TENURE_BASED' : 'PRICE_BASED'}
            offerType={singleEligiblePlan?.offerType}
            offerValidTill={singleEligiblePlan?.offerValidTill}
            baseMonths={resolveTenureBasedBaseMonths(singleEligiblePlan?.basePlanTenure)}
            extraMonths={resolveTenureBasedExtraMonths(singleEligiblePlan)}
          />
        ) : (
          <div className='px-4 py-2.5 font-medium leading-[1.3]'>
            <ElitePlanRenewLockInPriceOfferContent useTheirPricing={familyConfig?.isFamilyMode} />
          </div>
        )}
        <div ref={inlineRenewCtaRef}>
          <NewEliteLandingPrimaryCtaButton
            label={renewCtaLabel}
            disabled={isRenewCtaDisabled}
            onClick={handleRenewClick}
            className='w-full'
          />
        </div>
      </div>
      {renewalSupportDisplayHtml ? (
        <div role='status'>
          <RenderHTML
            innerHTMLText={renewalSupportDisplayHtml}
            className='mt-4 text-sm leading-[1.45] text-[#E77464] text-center mx-5 font-medium'
          />
        </div>
      ) : null}
      <AnimatePresence>
        {!isInlineRenewCtaVisible ? (
          <motion.div
            key='floating-renew-cta'
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className='fixed inset-x-0 z-40 mx-auto w-full max-w-[420px] px-5'
            style={{ bottom: `${getBottomPadding() + 16}px` }}
          >
            <NewEliteLandingPrimaryCtaButton
              label={renewCtaLabel}
              disabled={isRenewCtaDisabled}
              onClick={handleRenewClick}
              className='mx-0 w-full'
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )

}

export function ElitePlanRenewLockInPriceOfferContent({ useTheirPricing = false }: { useTheirPricing?: boolean }) {
  const pricePhrase = useTheirPricing ? 'their current price' : 'your current price'
  return (
    <>
      <p className='text-[11px] leading-[1.2] tracking-[0.04em]'>EXCLUSIVELY FOR YOU</p>
      <p className='mt-1 text-[13px] leading-[1.4]'>
        Renew now to <span className='font-semibold'>lock in {pricePhrase}</span>
      </p>
    </>
  )
}
